import clsx from 'clsx';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { BiSolidChevronDown, BiX } from 'react-icons/bi';

import s from '@/components/Inputs/inputs.module.scss';

const MAX_HEIGHT = 220;

interface EventOption {
  value: string;
  label: string;
}

interface EventSelectProps {
  label: string;
  placeholder?: string;
  options: EventOption[];
  selectedLabel: string | null;
  onSelect: (value: string | null) => void;
}

export function EventSelect({
  label,
  placeholder,
  options,
  selectedLabel,
  onSelect,
}: EventSelectProps) {
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState('');
  const [outerStyle, setOuterStyle] = useState<CSSProperties>();
  const [listMaxHeight, setListMaxHeight] = useState(MAX_HEIGHT);
  const wrapperRef = useRef<HTMLLabelElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoutRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!opened) return;

    const update = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;

      const spaceBelow = window.innerHeight - rect.bottom;
      const upwards = spaceBelow < MAX_HEIGHT && rect.top > spaceBelow;
      const calcMaxHeight = Math.max(
        Math.min(MAX_HEIGHT, upwards ? rect.top : spaceBelow) - 12,
        80,
      );

      setListMaxHeight(calcMaxHeight);
      setOuterStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        right: 'auto',
        zIndex: 10000,
        ...(upwards ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [opened]);

  useEffect(() => {
    if (!opened) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target) || popoutRef.current?.contains(target)) return;
      setOpened(false);
      setQuery('');
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [opened]);

  const search = query.trim().toLowerCase();
  const visible = search
    ? options.filter((option) => option.label.toLowerCase().includes(search))
    : options;

  return (
    <label className={s.InputRoot} ref={wrapperRef}>
      <div className={s.Label}>
        <span>{label}</span>
      </div>
      <div
        ref={anchorRef}
        className={clsx(s.InputWrapper, s.SelectWrapper, s.withIcon, opened && s.Opened)}
      >
        {selectedLabel && !opened ? (
          <span
            className={s.selectedValue}
            onClick={(event) => {
              event.preventDefault();
              onSelect(null);
            }}
          >
            <span>{selectedLabel}</span> <BiX size={16} />
          </span>
        ) : (
          <input
            type="text"
            className="body-medium"
            placeholder={placeholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={() => setOpened(true)}
          />
        )}
        <BiSolidChevronDown className={s.icon} />
      </div>

      {opened &&
        createPortal(
          <div className={s.Popout} ref={popoutRef} style={outerStyle}>
            <ul className={s.PopoutScroll} style={{ maxHeight: listMaxHeight }}>
              {visible.length === 0 ? (
                <li>No events match. Please try another search.</li>
              ) : (
                visible.map((option) => (
                  <li
                    key={option.value}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onSelect(option.value);
                      setQuery('');
                      setOpened(false);
                    }}
                  >
                    {option.label}
                  </li>
                ))
              )}
            </ul>
          </div>,
          document.body,
        )}
    </label>
  );
}
